import classnames from 'classnames';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import ScrollContainer from 'react-indiana-drag-scroll';
import { ListTitle } from '../ItemList/ListTitle';

import Hexagon from '../../Hexagon/Hexagon';
import { ChevronIcon, ChevronIconDirection } from '../ChannelListHexagon/ChevronIcon/ChevronIcon';
import { VideoItem } from '../ItemList/VideoItem';
import './VideoListSlider.scss';

const Placeholder = () => (
	<Hexagon className="ChannelThumbnail" size="none">
		<div style={{ background: 'black', opacity: 0.1, width: '100%', height: '100%' }} />
	</Hexagon>
);

export const VideoListSlider = ({
	videos,
	className,
	forwardedRef,
	placeholderLength,
	title,
}) => {
	const scrollContainer = useRef();
	const [isLoaded, setIsLoaded] = useState(false);

	const [isScrollable, setIsScrollable] = useState(
		{ right: true, left: true },
	);

	const videosOrPlaceholder = videos?.length > 0
		? videos
		: Array.from({ length: placeholderLength });

	const firstRowData = useMemo(() => (videosOrPlaceholder?.slice(0, 9)), [videosOrPlaceholder]);
	const secondRowData = useMemo(() => (videosOrPlaceholder?.slice(9)), [videosOrPlaceholder]);

	const checkScroll = useCallback(() => {
		if (scrollContainer.current) {
			setIsScrollable({
				left: scrollContainer.current.scrollLeft > 0,
				right: (scrollContainer.current.scrollLeft + scrollContainer.current.clientWidth)
					< (scrollContainer.current.scrollWidth),
			});
		}
	}, []);

	useLayoutEffect(() => {
		window.addEventListener('resize', checkScroll);
		checkScroll();
		return () => window.removeEventListener('resize', checkScroll);
	}, [checkScroll]);

	useLayoutEffect(() => {
		if (scrollContainer.current) {
			setTimeout(() => {
				scrollContainer.current.scrollLeft = (scrollContainer.current.scrollWidth / 2)
			- (scrollContainer.current.clientWidth / 2);
			}, 100);

			setIsLoaded(true);
		}
	}, []);

	const handleHorizontalScrollRight = () => {
		scrollContainer.current.scrollLeft += (scrollContainer.current.clientWidth);
	};

	const handleHorizontalScrollLeft = () => {
		scrollContainer.current.scrollLeft += (-scrollContainer.current.clientWidth);
	};

	return (
		<section
			className={classnames('VideoListSlider ChannelList ChannelListHexagon size-lg', className)}
			ref={forwardedRef}
		>
			{title && <span className="d-inline-block ml-5"><ListTitle title={title} /></span>}
			<ScrollContainer
				className={clsx('ChannelListHexagon__scrollContainer content-white', { isLoaded })}
				innerRef={scrollContainer}
				onEndScroll={checkScroll}
				vertical={false}
				key="VideoScroll"
			>
				<div className="ChannelListHexagon_list">
					{firstRowData.map((video, index) => (
						video ? (
							<VideoItem
								key={`${title}-video-list-${video._id}`}
								video={video}
							/>
						) : <Placeholder key={`placeholder-video-${index}`} />
					))}
					<div className="mr-5" />
				</div>
				<div className="ChannelListHexagon_list">
					{firstRowData.map((video, index) => (
						video ? (
							<VideoItem
								key={`${title}-video-list-${video._id}`}
								video={video}
							/>
						) : <Placeholder key={`placeholder-video-${index}`} />
					))}
					<div className="mr-5" />
				</div>
			</ScrollContainer>
			{
				isScrollable.left && (
					<span
						className="ChannelList_chevron_left color-yellow"
						onClick={handleHorizontalScrollLeft}
					>
						<div className="position-absolute start-100 translate-middle">
							<ChevronIcon />
						</div>
					</span>
				)
			}
			{
				isScrollable.right && (
					<span
						className="ChannelList_chevron_right color-yellow"
						onClick={handleHorizontalScrollRight}
					>
						<div className="position-absolute translate-middle">
							<ChevronIcon direction={ChevronIconDirection.RIGHT} />
						</div>
					</span>
				)
			}
		</section>
	);
};

VideoListSlider.propTypes = {
	videos: PropTypes.arrayOf(PropTypes.shape({})),
	className: PropTypes.string,
	forwardedRef: PropTypes.shape({}),
	placeholderLength: PropTypes.number,
	title: PropTypes.node,
};

VideoListSlider.defaultProps = {
	videos: [],
	className: '',
	forwardedRef: null,
	title: null,
	placeholderLength: 16,
};
